import type { HairlineFigure } from "./Hairline";
import {
  Cam,
  clamp,
  facing,
  fillet,
  fit,
  poly,
  prism,
  proj,
  rad,
  rings,
  seg,
  unproj,
} from "./iso";
import { tdone, tset, tval, tween } from "./motion";
import {
  disposer,
  mk,
  pointer,
  put,
  register,
  solid,
  type FigureEls,
  type FigureHandle,
} from "./stage";

/**
 * Ledger: an open account book of twelve ruled pages, a month each, on its
 * boards. Across the book the pointer picks a month: its page stands up, the
 * months before it fall to the left and the months after it to the right, and
 * the pages nearest it lean toward it, staggered outwards on the 700ms lift
 * curve. Each page is ruled with a header, lines, and a money column. At rest
 * nine months lie closed on the left and October is caught mid-turn, bright.
 * The slider is how far the neighbours lean.
 *
 * The pattern: one of many, as Riffle, on a hinge. Tweens of each page's
 * angle, a stagger and a falloff by distance, a paint order kept by moving
 * groups, and a hit test on the plane of the page blocks, which never moves.
 */

const K = 12,
  W = 50,
  D = 68,
  ZB = 5,
  FAN = 1.5,
  MONTHS = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
  ];
/** At rest: the angle of each page about the spine, 0 lying on the right, 180 on the left, each FAN degrees above the one under it; October caught mid-turn. */
const REST = MONTHS.map((_, k) =>
  k < 9 ? 178.5 - FAN * k : k === 9 ? 118 : 1.5 + FAN * (K - 1 - k),
);
const MARK = 9,
  FALL = [1, 0.45, 0.18, 0];
/** A page's outline in its own (u from the spine, v along it), rounded at its outer corners. */
const SHAPE = fillet(
  [
    [0, 0],
    [W, 0],
    [W, D],
    [0, D],
  ],
  [0.5, 2.6, 2.6, 0.5],
);

function mount({ stage, svg, read }: FigureEls, value: number): FigureHandle {
  const bag = disposer();
  let lean = value;

  const C = Cam(45, 0.5, 2.1);
  fit(
    C,
    [
      [-W - 5, -4, 0],
      [W + 5, D + 4, 0],
      [W + 5, -4, 0],
      [-W - 5, D + 4, 0],
      [0, 0, ZB + W],
      [0, D, ZB + W],
    ],
    200,
    166,
  );
  const P = proj(C),
    front = facing(C);
  const w = (u, v, th) => P(u * Math.cos(th), v, ZB + u * Math.sin(th));

  // The angle at which a page is seen edge on: pages are painted farthest from it first.
  let edge = 0,
    least = Infinity;
  for (let a = 0; a <= 180; a += 0.5) {
    const s = w(0, 0, rad(a)),
      e = w(W, 0, rad(a)),
      t = w(0, D, 0);
    const area = Math.abs(
      (e[0] - s[0]) * (t[1] - s[1]) - (e[1] - s[1]) * (t[0] - s[0]),
    );
    if (area < least) {
      least = area;
      edge = a;
    }
  }

  const g = mk("g", {}, svg);
  const [br, bi] = rings(-W - 5, -4, W + 5, D + 4, 4, 1.4);
  put(solid(g), prism(P, front, br, bi, 0, 2));
  for (const [x0, x1] of [
    [-W, -0.6],
    [0.6, W],
  ]) {
    const [r, i] = rings(x0, 0, x1, D, 2, 0.8);
    put(solid(g), prism(P, front, r, i, 2, ZB));
  }

  const leaves = mk("g", {}, g);
  const pages = REST.map((a, k) => {
    const grp = mk("g", {}, leaves);
    return {
      k,
      a: tween(a),
      grp,
      face: mk("path", { class: "sil" }, grp),
      rules: mk("path", { class: "nf lo" }, grp),
      col: mk("path", { class: "nf" }, grp),
      drawn: NaN,
    };
  });

  /** A page at angle a: its outline, its ruled lines, and its header and money column. */
  function draw(pg, a) {
    if (a === pg.drawn) return;
    pg.drawn = a;
    const th = rad(a),
      at = (u, v) => w(u, v, th);
    pg.face.setAttribute("d", poly(SHAPE.map(([u, v]) => at(u, v))));
    pg.rules.setAttribute(
      "d",
      [0.34, 0.46, 0.58, 0.7, 0.82]
        .map((f) => seg(at(4, D * f), at(W - 4, D * f)))
        .join(""),
    );
    pg.col.setAttribute(
      "d",
      seg(at(4, D * 0.2), at(W - 4, D * 0.2)) +
        seg(at(W * 0.7, D * 0.2), at(W * 0.7, D * 0.9)),
    );
  }

  let order = "";
  /** Back to front: the page farthest in angle from edge on first. Moves the groups only when the order changes. */
  function sort(now) {
    const by = pages
      .slice()
      .sort(
        (p, q) =>
          Math.abs(tval(q.a, now) - edge) - Math.abs(tval(p.a, now) - edge),
      );
    const key = by.map((p) => p.k).join(",");
    if (key !== order) {
      order = key;
      leaves.append(...by.map((p) => p.grp));
    }
  }

  const B = register(stage, (_dt, now) => {
    let moving = false;
    for (const pg of pages) {
      draw(pg, tval(pg.a, now));
      if (!tdone(pg.a, now)) moving = true;
    }
    sort(now);
    return moving;
  });
  bag.add(() => B.unregister());

  /** The month under a pointer on the plane of the page blocks, which never moves: left to right across the book. */
  function hit([sx, sy]) {
    const [wx, wy] = unproj(C, sx, sy, ZB);
    if (wy < -12 || wy > D + 12 || Math.abs(wx) > W + 10) return -1;
    return clamp(Math.round(((wx + W) / (2 * W)) * (K - 1)), 0, K - 1);
  }

  let act = -1;
  /** Stands month m up (-1 lays every page back): the others fall to their side, the nearest leaning toward it, later the further away. */
  function setActive(m) {
    if (m === act) return;
    const now = performance.now(),
      from = m >= 0 ? m : act;
    act = m;
    for (const pg of pages) {
      const far = Math.abs(pg.k - from),
        tilt =
          1.5 +
          FAN * (pg.k < m ? pg.k : K - 1 - pg.k) +
          lean * FALL[Math.min(far, 3)];
      const to =
        m < 0 ? REST[pg.k] : pg.k === m ? 90 : pg.k < m ? 180 - tilt : tilt;
      tset(pg.a, to, now, far * 45);
      pg.face.classList.toggle("hi", m < 0 ? pg.k === MARK : pg.k === m);
    }
    read.textContent = m < 0 ? "rest" : MONTHS[m];
    B.wake();
  }

  pages[MARK].face.classList.add("hi");
  bag.add(
    pointer(stage, {
      move: (p) => setActive(hit(p)),
      leave: () => setActive(-1),
    }),
  );
  bag.add(() => svg.replaceChildren());

  return {
    set: (v) => {
      lean = v;
      if (act >= 0) {
        const m = act;
        act = -1;
        setActive(m);
      }
    },
    destroy: () => bag.dispose(),
  };
}

export const LEDGER: HairlineFigure = {
  name: "ledger",
  means:
    "An open account book of twelve months: across it the pointer stands one month's page up, and the pages beside it lean toward it.",
  range: [10, 26, 42],
  tour: [[153, 161], [261, 215], [207, 188], null],
  mount,
};
